import { SegmentedTabs } from '@skupreme/shared/ui';
import { useSearchParams } from 'react-router';
import { useMappingCounts } from '../../hooks/use-mapping-counts';
import { useRefreshSuggestions } from '../../hooks/use-refresh-suggestions';
import type { MappingView } from '../../lib/suggestion-types';
import { GatedButton } from './GatedButton';
import { MappedView } from './MappedView';
import { SuggestionsView } from './SuggestionsView';

const VIEWS: {
  id: MappingView;
  label: string;
  count?: 'toReview' | 'noMatch' | 'skipped';
}[] = [
  { id: 'to_review', label: 'To review', count: 'toReview' },
  { id: 'no_match', label: 'No match', count: 'noMatch' },
  { id: 'skipped', label: 'Skipped', count: 'skipped' },
  { id: 'mapped', label: 'Mapped' },
];

const DEFAULT_VIEW: MappingView = 'to_review';

/** When the account's suggestions were last computed, in words. */
export const lastCheckedText = (at: string | null | undefined) =>
  at
    ? `Last checked ${new Date(at).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })}`
    : 'Not checked yet';

/** The `?view=` of the URL, or the default for a missing or unknown one. */
export const viewOf = (value: string | null): MappingView =>
  VIEWS.find(({ id }) => id === value)?.id ?? DEFAULT_VIEW;

export type MappingTabProps = {
  /** The user can't change mappings: actions show, disabled with a tooltip. */
  isReadOnly?: boolean;
  /** The plan hides the actions altogether. */
  isActionsHidden?: boolean;
};

/**
 * The self-serve Mapping tab (EN-3966): the listings a person has to link to
 * products, in four views (To review, No match, Skipped, Mapped) kept in the URL.
 */
export function MappingTab({ isReadOnly, isActionsHidden }: MappingTabProps) {
  const [params, setParams] = useSearchParams();
  const view = viewOf(params.get('view'));
  const { data: counts } = useMappingCounts();
  const refresh = useRefreshSuggestions();

  return (
    <div className="sk:flex sk:flex-col sk:gap-3">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:justify-between sk:gap-3">
        <SegmentedTabs
          label="Mapping view"
          selectedKey={view}
          onSelectionChange={key => setParams({ view: key })}
          items={VIEWS.map(({ id, label, count }) => ({
            id,
            label: count && counts ? `${label} (${counts[count]})` : label,
          }))}
        />
        {isActionsHidden ? null : (
          <div className="sk:flex sk:items-center sk:gap-3">
            {counts ? (
              <span className="sk:text-sm sk:text-gray-500">
                {lastCheckedText(counts.lastRefreshedAt)}
              </span>
            ) : null}
            <GatedButton
              variant="pill"
              size="sm"
              isReadOnly={isReadOnly}
              isDisabled={refresh.isPending}
              onPress={() => refresh.mutate()}
            >
              {refresh.isPending ? 'Checking…' : 'Check again'}
            </GatedButton>
          </div>
        )}
      </div>
      {view === 'mapped' ? (
        <MappedView isReadOnly={isReadOnly} isActionsHidden={isActionsHidden} />
      ) : (
        <SuggestionsView
          key={view}
          view={view}
          counts={counts}
          isReadOnly={isReadOnly}
          isActionsHidden={isActionsHidden}
        />
      )}
    </div>
  );
}
